import { ParserBuilder } from 'nuqs'

import { BaseValue, getValidationSchemeFromConfig, StorableType } from '../validation'
import { createUrlStorageHook } from './createUrlStorageHook'
import { getParserWithValidation } from './parsers'
import { UseUrlHookOptions } from './useUrlStorageState'

/**
 * @example
 *
 * ```tsx
 * const FiltersUrlStorage = createUrlStorage('filters', {
 *   type: 'object',
 *   validation: {
 *     schema: z.any(),
 *   },
 * })
 *
 * const Example_1 = () => {
 *   // Читаем и изменяем состояние
 *   const [filters, setFilters] = FiltersUrlStorage.useState({})
 * }
 *
 * const Example_2 = () => {
 *   // Сериализуем состояние
 *   const onClick = (filters: any) => {
 *     const search = FiltersUrlStorage.serialize(filters)
 *   }
 * }
 * ```
 */
export function createUrlStorage<TType extends StorableType, TValue extends BaseValue<TType>>(
  key: string,
  options: UseUrlHookOptions<TType, TValue>,
) {
  const { type, validation, clearOnDefault, onValidationError, defaultValue = null } = options

  const scheme = getValidationSchemeFromConfig<TType, TValue>(type, validation)

  const parser = getParserWithValidation<TType, TValue>({
    clearOnDefault,
    defaultValue,
    scheme,
    type,
    onValidationError,
  })

  const useState = createUrlStorageHook(key, options)

  const serialize = createUrlSerializer(key, parser)

  return {
    useState,
    serialize,
  }
}

function createUrlSerializer<TValue>(key: string, parser: ParserBuilder<TValue>) {
  return (value: TValue) => {
    const params = new URLSearchParams()

    params.append(key, parser.serialize(value))

    return params.toString()
  }
}
